/**
 * Shared vocabulary for every calculator.
 *
 * Two rules hold across this whole directory:
 *
 *  1. Nothing here touches the DOM, reads globals, or does I/O. Calculators are pure
 *     functions of their arguments. That is what makes them testable, and the tests are
 *     the only thing between a user and a wrong dilution ratio.
 *
 *  2. Errors are *codes*, never sentences. The site is bilingual; a calculator that
 *     returned "Target ABV must be lower" would be untranslatable. Codes are looked up
 *     in `src/lib/i18n/` at render time.
 */

export type Unit = 'percent' | 'litre' | 'celsius' | 'kilogram';

/** A calculation that either produced numbers or explained why it could not. */
export type CalcResult<T> = { ok: true; value: T } | { ok: false; error: CalcError };

export interface CalcError {
  /** i18n lookup key, e.g. `error.targetAboveSource`. */
  code: string;
  /** Numbers interpolated into the translated message. */
  params?: Record<string, number>;
  /** Field this error belongs to, so the form can highlight the offending input. */
  field?: string;
}

export const ok = <T>(value: T): CalcResult<T> => ({ ok: true, value });

export const err = <T>(
  code: string,
  params?: Record<string, number>,
  field?: string,
): CalcResult<T> => ({ ok: false, error: { code, params, field } });

/**
 * One numeric input. The generic form renderer builds every calculator's UI from these,
 * so a new calculator needs no bespoke markup and no second copy of its form in the other
 * language.
 */
export interface FieldDef {
  name: string;
  unit: Unit;
  /**
   * Intended range and granularity.
   *
   * Documentation, not enforcement: the form renders plain text inputs — see
   * `calculatorMarkup.ts` for why — so nothing checks these. Every real bound is asserted
   * inside `compute`, which is the only place that can also name the right error code.
   */
  min: number;
  max: number;
  step: number;
  defaultValue: number;
}

export interface OutputDef {
  name: string;
  unit: Unit;
  /** Decimal places to display. Deliberately modest — see `notesKey` on each calculator. */
  precision: number;
  /** Marks the headline number, rendered larger than the rest. */
  primary?: boolean;
}

export type Inputs = Record<string, number>;
export type Outputs = Record<string, number>;

export interface CalculatorDef {
  id: string;
  fields: readonly FieldDef[];
  outputs: readonly OutputDef[];
  /**
   * i18n key for the method/accuracy caveat shown under the results. Every calculator has
   * one; none of these methods is exact and saying so is part of the product.
   */
  notesKey: string;
  compute(inputs: Inputs): CalcResult<Outputs>;
}

/**
 * Read one field out of raw form input.
 *
 * A field the form never supplied comes back as `NaN`, which {@link checkFinite} then
 * turns into a normal `error.notANumber` — the same path an empty text box takes. That
 * keeps a malformed page from silently computing with `undefined`.
 */
export const num = (inputs: Inputs, name: string): number => inputs[name] ?? Number.NaN;

/**
 * Guard shared by every calculator: reject NaN/Infinity before doing arithmetic on it.
 * An empty text input parses to NaN, so this is the first thing every `compute` runs.
 */
export function checkFinite(inputs: Inputs): CalcError | null {
  for (const [name, value] of Object.entries(inputs)) {
    if (!Number.isFinite(value)) return { code: 'error.notANumber', field: name };
  }
  return null;
}
